import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, Renderer2, ViewChild } from '@angular/core';
import { NoteService } from '../shared/note.service';
import { FilterMatchMode, SelectItem, SortEvent, SortMeta } from 'primeng/api';
import { fromEvent, of, Subscription, timer } from 'rxjs';
import { catchError, finalize, map, takeWhile } from 'rxjs/operators';
import { PrimeNGHelpersService } from '../../core/services/primeng-helpers.service';
import { TableExtended } from '../../shared/model/primeng-extensions.model';
import { UntypedFormControl } from '@angular/forms';
import { LogService } from '../../logs/log.service';
import { NoteColumn, NotesTableDataRow, SortField } from './notes-table.model';
import { NotesTableService } from './notes-table.service';
import { Note } from "../shared/note.model";
import { ColumnFilter } from 'primeng/table';
import { AppStateService } from '../../core/services/app-state.service';
import { Utils } from '../../core/utils';
import { NoteAction, NoteActionType } from "ng/src/app/notes/note-actions/note-actions.model";
import { NotesTableCaptionComponent } from './notes-table-caption.component';
import { DisplayNote } from "ng/src/app/notes/note-display/note-display.model";

@Component({
	selector: 'notes-table',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [NotesTableService],
    templateUrl: './notes-table.component.html',
    styleUrls: ['./notes-table.component.css']
})
export class NotesTableComponent implements OnInit, AfterViewInit, OnDestroy {
	@Input() downloadCSV?: (showComments: boolean) => void;
	@Input() defaultColumnKeys!: NoteColumn[];
	@Input() enableAutoScroll!: boolean;
	@Input() enableColumnFiltering = true;
	@Input() enableReportFiltering = true;
	@Input() exportMessage = 'Waiting for notes to finish loading...';
	@Input() getPDFReportQueryParams!: () => { [queryParam: string]: string };
	@Input() lastUpdate?: { date: Date, success: boolean };
	@Input() loading = false;
	@Input() notesMessage!: string;
	@Input() pollBased = true;
	@Input() savedPreferencesKey!: string;
	@Input() scrollTopWaitTimeMinutes = 5;
	@Input() sortMode?: 'single' | 'multiple';
	@Input() showMaximizeButton = false;
	@Input() turnoverFilter = false;
	@Input() useNoteActions = true;
	@Input() verticalViewNameField = 'createdBy';

	@Input()
	get notes() {
		return this._notes;
	}
	set notes(notes: NotesTableDataRow[]) {
		if (this.table)
			this.table.suppressResetScrollTop = true;
		
		this._notes = notes;
	}
	
	@Input()
	get maximized() {
		return this._maximized;
	}
	set maximized(val) {
		this._maximized = val;
		this.maximizedChange.emit(val);
	}

	@Output() afterViewInit = new EventEmitter<NotesTableComponent>();
	@Output() cancelQueryClicked = new EventEmitter();
	@Output() maximizedChange = new EventEmitter<boolean>();
	@Output() noteTimeClicked = new EventEmitter<{ rowIndex: number, href: string, note: Note }>();
	@Output() onNoteAction = new EventEmitter<{type: NoteActionType, note: Note}>();
	@Output() refreshQueryClicked = new EventEmitter();
	@Output() reportFilterChanged = new EventEmitter<number | null>();
	@Output() scrollPageReached = new EventEmitter<number>();
	@Output() sortChanged = new EventEmitter<{ multisortmeta: SortMeta[] }>();
	@Output() timeframeFilterChanged = new EventEmitter<{ matchMode?: FilterMatchMode, date: Date | null }>();
	@Output() turnoverFilterChanged = new EventEmitter<boolean>();
	
	// Constant for widths at which to show card view. Derived from resizing note-table with various active 
	// column counts and noting the width at which we should start showing cards.
	private readonly COLUMN_BREAKPOINTS: { [columnCount: number]: number } = {
		1: 397, 2: 527, 3: 588, 4: 741, 5: 841, 6: 998, 7: 1089
	};
	// First two columns (timeframe and turnover) in the template will always be rendered. Then, the columns
	// passed in below will follow in their array order. Some columns can be toggled on demand in the UI.
	private _columnKeys!: NoteColumn[];
	private lastAppliedFilterDate: Date | null = null;
	private nativeEl: HTMLElement;
	private _notes: NotesTableDataRow[] = [];
	private scrollTopSubscription?: Subscription;
	private resizeSub?: Subscription;
	private turnoverMap: { [key: number]: UntypedFormControl } = {};
	private _maximized = false;
	
	private get savedColumnsKey() {
		return `${this.savedPreferencesKey}-columns`;
	}
	
	@ViewChild('Table') table?: TableExtended;
	@ViewChild('TimeframeFilter') timeframeFilter!: ColumnFilter;
	@ViewChild(NotesTableCaptionComponent) captionComponent!: NotesTableCaptionComponent;
	
	containerElement?: Element;
	displayNoteActions: Record<string | number, boolean> = {};
	initSort: SortMeta[] = [];
	noteActions: NoteAction[] = NoteAction.defaultSet();
	readOnlyUser = LogService.selectedLog?.role == 'READ';
	scrollTop = 0;
	showAllAcks = false;
	showingComments: Record<number, boolean> = {};
	showPriorityColumn = false;
	showAcknowledgmentsColumn = false;
	showUpdatedColumn = false;
	showCreatedByColumn = false;
	showUpdatedByColumn = false;
	showTagsColumn = false;
	sortIconTooltips!: Record<SortField, string>;
	
	timeframeFilterDate: Date | null = null;
	timeframeFilterOptions: SelectItem[] = [
		{ label: 'Before', value: FilterMatchMode.DATE_BEFORE },
		{ label: 'After', value: FilterMatchMode.DATE_AFTER }
	];
	timeframeFilterTooltip = 'Filter by Timeframe';
	turnoverTooltip = 'Turnover and Non-Turnover Notes';
	usingFirefox = AppStateService.usingFirefox;
	
	get activeNoteFilterReportId()  {
		return this.notesTableService.activeNoteFilterReportId;
	}

	get buttonState() {
		return this.captionComponent.buttonState;
	}

	get columnKeys() {
		return this._columnKeys;
	}
	set columnKeys(val) {
		this._columnKeys = val;
		this.showPriorityColumn = false;
		this.showAcknowledgmentsColumn = false;
		this.showTagsColumn = false;
		this.showUpdatedColumn = false;
		this.showUpdatedByColumn = false;
		this.showCreatedByColumn = false;

		this.updateShownColumns();
	}

	get showVerticalView() {
		return this.captionComponent?.showVerticalView;
	}
	set showVerticalView(val) {
		this.captionComponent && (this.captionComponent.showVerticalView = val);
	}

	showNoteActions(noteId: number) {
		return this.useNoteActions &&
			this.buttonState != 'DISABLED' &&
			(this.buttonState == 'VISIBLE' || this.displayNoteActions[noteId]);
	}
	
	noteActionsSticky(noteId: number) {
		return this.buttonState == 'HOVER' ||
			this.displayNoteActions[noteId] ||
			(!AppStateService.hoverSupported && this.buttonState != 'DISABLED');
	}

	constructor(
		private renderer: Renderer2,
		private primeNgHelpersService: PrimeNGHelpersService,
		public notesTableService: NotesTableService,
		public noteService: NoteService,
		private cd: ChangeDetectorRef,
		elRef: ElementRef)
	{
		this.renderer.addClass(document.body, 'p-mb-0');
		this.nativeEl = elRef.nativeElement;
	}
	
	ngOnInit() {
		if (this.sortMode) {
			this.initSort = NotesTableService.getDefaultSorts();
			this.showPriorityColumn = this.initSort.some(s => s.field == 'maxPriority');
		}

		this.columnKeys = AppStateService.preferences.getValue(this.savedColumnsKey, this.defaultColumnKeys);
		this.enableAutoScroll = this.enableAutoScroll ?? this.pollBased;
		
		this.containerElement = this.table!.el.nativeElement.querySelector('.p-datatable-scrollable-body');
	}
	
    ngAfterViewInit() {
		this.primeNgHelpersService.enableTableSuppressScrollTop(this.table!);

		if (this.timeframeFilter) {
			this.primeNgHelpersService.registerServerSideColumnFilter(
				this.timeframeFilter,
				this.onApplyTimeframeFilter,
				this.onClearTimeframeFilter,
				this.onHideTimeframeFilter
			);
		}

	    this.afterViewInit.emit(this);
	}

	ngOnDestroy() {
		this.scrollTopSubscription?.unsubscribe();
		this.resizeSub?.unsubscribe();
		this.renderer.removeClass(document.body, 'p-mb-0');
	}
	
	updateShownColumns() {
		AppStateService.preferences.setValue(this.savedColumnsKey, this._columnKeys);

		this._columnKeys.forEach(val => {
			switch (val.field) {
				case 'priority':
					this.showPriorityColumn = val.show;
					break;
				case 'tags':
					this.showTagsColumn = val.show;
					break;
				case 'acknowledgments':
					this.showAcknowledgmentsColumn = val.show;
					break;
				case 'updated':
					this.showUpdatedColumn = val.show;
					break;
				case 'updatedBy':
					this.showUpdatedByColumn = val.show;
					break;
				case 'createdBy':
					this.showCreatedByColumn = val.show;
					break;
			}
		});

		this.onResize();
	}

	onShowDefaultColumnsClicked() {
		this.columnKeys = this.defaultColumnKeys;
	}
	
	getTurnoverControl(note: Note): UntypedFormControl {
		if (!this.turnoverMap[note.id]) {
			this.turnoverMap[note.id] = new UntypedFormControl({
				value: note.turnover,
				disabled: this.readOnlyUser
			});
		}

		return this.turnoverMap[note.id];
	}

	onSortChanged(event: { multisortmeta: SortMeta[] }) {
		const multisortHint = `(${AppStateService.modifierKey}-click to multisort)`,
			sortIconTooltips: Record<SortField, string> = {
				timeframe: 'Sort by Timeframe ' + multisortHint,
				maxPriority: 'Sort by Priority ' + multisortHint,
				createdBy: 'Sort by Source ' + multisortHint,
				updatedBy: 'Sort by Updated By ' + multisortHint
			},
			sortFields = event.multisortmeta.map(sm =>
				sortIconTooltips[sm.field as SortField]
					.replace('Sort by ', '')
					.replace(multisortHint, sm.order == 1 ? 'asc' : 'desc')
			),
			sortMessage = 'Sorted by ' + sortFields.join(', then by ');

		event.multisortmeta.forEach(sm => sortIconTooltips[sm.field as SortField] = sortMessage);
		this.sortIconTooltips = sortIconTooltips;

		this.sortChanged.emit(event);
    }

	onTurnoverFilterChanged(turnover: boolean) {
		this.turnoverFilterChanged.emit(turnover);

		this.turnoverTooltip = turnover ? 'Turnover Notes Only' : 'Turnover and Non-Turnover Notes';
	}

	/** Decides between table or card view based on number of columns shown and this component's width */
	onResize() {
		const width = this.nativeEl.getBoundingClientRect().width;

		this.showVerticalView = width < this.COLUMN_BREAKPOINTS[this.columnKeys.filter(c => c.show).length];
		
		this.cd.detectChanges();
	}

    toggleTurnover(note: NotesTableDataRow) {
		const oValue = note.turnover,
			control = this.getTurnoverControl(note);
		
		control.disable();
		
        this.noteService.patchNote(note, { turnover: !note.turnover })
            .pipe(
                map(r => !oValue),
                catchError(err => of(oValue)),
                finalize(() => control.enable())
            )
            .subscribe(value => control.setValue(note.turnover = value));
    }

    onScrollPageReached() {
		this.scrollPageReached.emit(this.scrollTop);
    }

    onScrollChanged(scrollTop: number) {
		this.scrollTop = scrollTop;
	    
		this.scrollTopSubscription?.unsubscribe();
		this.scrollTopSubscription = timer(0, 60000)
			.pipe(
				takeWhile(val =>
					this.enableAutoScroll &&
					val <= this.scrollTopWaitTimeMinutes &&
					this.scrollTop > 0)
			)
			.subscribe((val) => {
				if (val < this.scrollTopWaitTimeMinutes)
					return;

				this.table!.suppressResetScrollTop = false;
				this.scrollToTop();
			});
    }

    sortNotes(event: SortEvent) {
		this.notesTableService.sortNotes(event);
	}
	
	onReportFilterChanged(reportId: number | null) {
		this.reportFilterChanged.emit(reportId);
	}

	onCancelQueryClicked() {
		this.cancelQueryClicked.emit();
	}

	onRefreshQueryClicked() {
		this.refreshQueryClicked.emit();
	}

	onExpandCollapseContentClicked(expand: boolean) {
		this.showAllAcks = expand;

		for (const note of this.notes) {
			if (note.safeHtmlNoteAbridged)
				DisplayNote.toggleShowing(note, !expand);
			
			if (note.noteComments.length)
				this.showingComments[note.id] = expand;
		}
	}

	onNoteTimeClicked(rowIndex: number, note: Note, event: Event) {
		// If user has meta or control key pressed, allow original event to open the link in a new tab
		if (event instanceof MouseEvent && (event.metaKey || event.ctrlKey))
			return;

		event.preventDefault();
		this.noteTimeClicked.emit({
			rowIndex: rowIndex,
			href: note._links.self.href,
			note: note
		});
	}

    scrollToTop() {
        this.table!.resetScrollTop();
    }

    noteCarryForwardEnded(note: NotesTableDataRow): boolean {
		return !note.carryForwardEndTime || new Date(note.carryForwardEndTime) < new Date();
	}

	noteActionEvent = (function (this: NotesTableComponent, type: NoteActionType, note: Note) {
		this.onNoteAction.emit({type, note});
	}).bind(this);

	private onApplyTimeframeFilter = (matchMode: string): boolean => {
		if (!this.timeframeFilterDate && !this.lastAppliedFilterDate)
			return false;

		this.timeframeFilterChanged.emit({
			matchMode: matchMode,
			date: this.timeframeFilterDate
		});

		const beforeAfter = matchMode == FilterMatchMode.DATE_AFTER ? 'After' : 'Before';

		this.lastAppliedFilterDate = this.timeframeFilterDate;
		this.timeframeFilterTooltip = this.timeframeFilterDate ?
			`Filtered by Timeframe ${beforeAfter} ${Utils.formatDateString(this.timeframeFilterDate!)}` :
			'Filter by Timeframe';

		return !!this.lastAppliedFilterDate;
	}

	private onClearTimeframeFilter = () => {
		if (!this.lastAppliedFilterDate)
			return;
		
		this.timeframeFilterDate = null;
		this.timeframeFilterTooltip = 'Filter by Timeframe';
		this.lastAppliedFilterDate = null;

		this.timeframeFilterChanged.emit({ date: null });
	}

	// Restores current filter date if user enters invalid characters and closes filter overlay
	private onHideTimeframeFilter = (clearClicked: boolean) => {
		if (!clearClicked && !this.timeframeFilterDate)
			this.timeframeFilterDate = this.lastAppliedFilterDate;
	}
}